import React, { useState, useEffect } from 'react';
import { API_LINK } from '../../apiLink/apiLink';
import Layout from '../../components/Layout';

const AdminLoginManagement = ({ onNavigate, userInfo = {} }) => {
  // Mobile state (used for responsive content layout)
  const [isMobile, setIsMobile] = useState(false);

  // State for credentials data
  const [allCredentials, setAllCredentials] = useState([]); // Store all credentials
  const [filteredCredentials, setFilteredCredentials] = useState([]); // Store filtered results
  const [displayedCredentials, setDisplayedCredentials] = useState([]); // Store paginated results
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination and search state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [limit] = useState(10);

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedCredential, setSelectedCredential] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    role: 'student'
  });

  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 1024);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fetch all credentials from backend (without search parameters)
  const fetchAllCredentials = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        setError('Authentication token not found. Please login again.');
        setLoading(false);
        return;
      }

      // Fetch a large number of credentials (or implement multiple API calls if needed)
      const apiUrl = `${API_LINK}/api/admin/login-credentials?page=1&limit=1000`;

      console.log('Fetching all credentials from API...');

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();

        if (response.status === 401) {
          setError('Session expired. Please login again.');
          localStorage.removeItem('token');
          sessionStorage.removeItem('token');
          return;
        } else if (response.status === 404) {
          setError('Login credentials API endpoint not found. Please check if the backend server is running correctly.');
          return;
        } else if (response.status === 500) {
          setError('Server error occurred. Please check the backend logs.');
          return;
        }

        throw new Error(`Server error: ${response.status} - ${errorText}`);
      }

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        setError('Server returned invalid response format. Expected JSON.');
        return;
      }

      const result = await response.json();
      console.log('API Response:', result);

      if (result.success) {
        const credentialsData = result.data?.credentials || [];

        // Store all credentials
        setAllCredentials(credentialsData);

        console.log(`Loaded ${credentialsData.length} credentials from server`);

        // Apply initial filtering (empty filters show all)
        applyFilters(credentialsData, '', '');

      } else {
        setError(result.message || 'Failed to fetch credentials from server');
      }
    } catch (error) {
      console.error('Fetch credentials error:', error);
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        setError('Cannot connect to backend server. Please ensure:\n1. Backend server is running \n2. Admin API routes are properly configured\n3. Database connection is working');
      } else if (error.message.includes('JSON')) {
        setError('Server returned invalid data format. Please check backend API.');
      } else {
        setError(`Failed to load credentials: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Client-side filtering function
  const applyFilters = (credentials, searchText, roleText) => {
    let filtered = [...credentials];

    // Apply search filter (case-insensitive)
    if (searchText && searchText.trim()) {
      const searchLower = searchText.trim().toLowerCase();
      filtered = filtered.filter(credential =>
        credential.username.toLowerCase().includes(searchLower) ||
        credential.role.toLowerCase().includes(searchLower)
      );
    }

    // Apply role filter
    if (roleText && roleText.trim()) {
      filtered = filtered.filter(credential =>
        credential.role === roleText
      );
    }

    console.log(`Filtered ${filtered.length} credentials from ${credentials.length} total`);

    // Update filtered credentials
    setFilteredCredentials(filtered);
    setTotalCount(filtered.length);

    // Calculate pagination
    const totalPagesCalc = Math.ceil(filtered.length / limit);
    setTotalPages(totalPagesCalc);

    // Reset to first page when filters change
    setCurrentPage(1);

    // Apply pagination to filtered results
    applyPagination(filtered, 1);
  };

  // Client-side pagination function
  const applyPagination = (filteredData, page) => {
    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;
    const paginatedData = filteredData.slice(startIndex, endIndex);

    setDisplayedCredentials(paginatedData);
    setCurrentPage(page);

    console.log(`Showing page ${page}: ${paginatedData.length} credentials (${startIndex + 1}-${Math.min(endIndex, filteredData.length)} of ${filteredData.length})`);
  };

  // Initial data fetch
  useEffect(() => {
    fetchAllCredentials();
  }, []);

  // Handle search and role filter changes
  useEffect(() => {
    if (allCredentials.length > 0) {
      console.log('Applying filters:', { searchTerm, roleFilter });
      applyFilters(allCredentials, searchTerm, roleFilter);
    }
  }, [searchTerm, roleFilter, allCredentials]);

  // Handle pagination changes
  const handlePageChange = (page) => {
    if (page && page >= 1 && page <= totalPages) {
      applyPagination(filteredCredentials, page);
    }
  };

  // Handle search manually (for search button)
  const handleSearch = () => {
    console.log('Manual search triggered with term:', searchTerm, 'and role:', roleFilter);
    if (allCredentials.length > 0) {
      applyFilters(allCredentials, searchTerm, roleFilter);
    }
  };

  // Clear search function
  const clearSearch = () => {
    setSearchTerm('');
    setRoleFilter('');
    if (allCredentials.length > 0) {
      applyFilters(allCredentials, '', '');
    }
  };

  // Refresh credentials (re-fetch from server)
  const refreshCredentials = () => {
    fetchAllCredentials();
  };

  // Validate form
  const validateForm = () => {
    const errors = {};

    if (!formData.username.trim()) errors.username = 'Username is required';
    if (!selectedCredential && !formData.password.trim()) errors.password = 'Password is required';
    if (!formData.role.trim()) errors.role = 'Role is required';

    // Username validation
    if (formData.username && formData.username.length < 3) {
      errors.username = 'Username must be at least 3 characters';
    }

    // Password validation (only if password is provided)
    if (formData.password && formData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission for add/edit
  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setSubmitting(true);

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const url = selectedCredential
        ? `${API_LINK}/api/admin/login-credentials/${selectedCredential.id}`
        : `${API_LINK}/api/admin/login-credentials`;

      const method = selectedCredential ? 'PUT' : 'POST';

      // Prepare form data - don't send empty password for updates
      const submitData = { ...formData };
      if (selectedCredential && !submitData.password.trim()) {
        delete submitData.password;
      }

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submitData)
      });

      const result = await response.json();

      if (result.success) {
        setShowAddModal(false);
        setShowEditModal(false);
        resetForm();

        // Refresh credentials after successful add/edit
        await fetchAllCredentials();

        alert(selectedCredential ? 'Login credentials updated successfully!' : 'Login credentials added successfully!');
      } else {
        alert(result.message || 'Operation failed');
      }
    } catch (error) {
      console.error('Submit error:', error);
      alert('Failed to save credentials. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!selectedCredential) return;

    try {
      setSubmitting(true);

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const response = await fetch(`${API_LINK}/api/admin/login-credentials/${selectedCredential.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();

      if (result.success) {
        setShowDeleteModal(false);
        setSelectedCredential(null);

        // Refresh credentials after successful delete
        await fetchAllCredentials();

        alert('Login credentials deleted successfully!');
      } else {
        alert(result.message || 'Delete failed');
      }
    } catch (error) {
      console.error('Delete error:', error);
      alert('Failed to delete credentials. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      username: '',
      password: '',
      role: 'student'
    });
    setFormErrors({});
    setSelectedCredential(null);
    setShowPassword(false);
  };

  // Handle edit click
  const handleEditClick = (credential) => {
    setSelectedCredential(credential);
    setFormData({
      username: credential.username || '',
      password: '', // Don't populate password for security
      role: credential.role || 'student'
    });
    setShowEditModal(true);
  };

  // Handle view click
  const handleViewClick = (credential) => {
    setSelectedCredential(credential);
    setShowViewModal(true);
  };

  // Handle delete click
  const handleDeleteClick = (credential) => {
    setSelectedCredential(credential);
    setShowDeleteModal(true);
  };



  // Get role badge color
  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin':
        return { backgroundColor: '#fee2e2', color: '#dc2626' };
      case 'teacher':
        return { backgroundColor: '#e0f2fe', color: '#0277bd' };
      case 'student':
        return { backgroundColor: '#e8f5e9', color: '#2e7d32' };
      case 'hod':
        return { backgroundColor: '#fff3e0', color: '#f57c00' };
      case 'parent':
        return { backgroundColor: '#f3e5f5', color: '#7b1fa2' };
      default:
        return { backgroundColor: '#f5f5f5', color: '#666' };
    }
  };

  // Mask password for display
  const maskPassword = (password) => {
    return '•'.repeat(Math.min(password?.length || 8, 12));
  };

  return (
    <Layout role="admin" onNavigate={onNavigate} activePage="admin-login-credentials" userInfo={userInfo}>
      {/* Welcome Banner */}
      <div style={{
        background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
        color: '#ffffff',
        padding: '2rem',
        borderRadius: '12px',
        marginBottom: '2rem',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
      }}>
        <h2 style={{ margin: '0 0 0.5rem 0', fontSize: '1.5rem' }}>
          Login Credentials Management 🔐
        </h2>
        <p style={{ margin: 0, opacity: 0.9 }}>
          Manage user login credentials, create new accounts, and maintain secure access to the system.
        </p>
      </div>

      {/* Search and Controls Section */}
      <div style={{
        background: '#ffffff',
        padding: '2rem',
        borderRadius: '12px',
        marginBottom: '2rem',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'center',
          gap: '1rem',
          marginBottom: '1rem'
        }}>
          {/* Enhanced Search Bar */}
          <div style={{ flex: 1, maxWidth: isMobile ? '100%' : '300px' }}>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                placeholder="Search by username or role..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                style={{
                  width: '100%',
                  padding: '1rem 4rem 1rem 1rem',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  outline: 'none',
                  transition: 'all 0.3s ease'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#238548';
                  e.target.style.boxShadow = '0 0 0 3px rgba(35, 133, 72, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e2e8f0';
                  e.target.style.boxShadow = 'none';
                }}
              />
              <div style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', display: 'flex', gap: '5px' }}>
                {(searchTerm || roleFilter) && (
                  <button
                    onClick={clearSearch}
                    style={{
                      background: 'none',
                      border: 'none',
                      fontSize: '1rem',
                      cursor: 'pointer',
                      color: '#666',
                      padding: '0.3rem'
                    }}
                    title="Clear search"
                  >
                    ✕
                  </button>
                )}
                <button
                  onClick={handleSearch}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '1.2rem',
                    cursor: 'pointer',
                    color: '#666',
                    padding: '0.3rem'
                  }}
                  title="Search"
                >
                  🔍
                </button>
              </div>
            </div>
          </div>

          {/* Role Filter */}
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            style={{
              padding: '1rem',
              border: '2px solid #e2e8f0',
              borderRadius: '8px',
              fontSize: '1rem',
              outline: 'none',
              minWidth: '120px'
            }}
          >
            <option value="">All Roles</option>
            <option value="admin">Admin</option>
            <option value="teacher">Teacher</option>
            <option value="student">Student</option>
            <option value="hod">HOD</option>
            <option value="parent">Parent</option>
          </select>

          {/* Add Credentials Button */}
          <button
            onClick={() => {
              resetForm();
              setShowAddModal(true);
            }}
            style={{
              padding: '1rem 2rem',
              background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
              color: '#ffffff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              whiteSpace: 'nowrap'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 8px 25px rgba(35, 133, 72, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = 'none';
            }}
          >
            <span style={{ fontSize: '1.2rem' }}>+</span>
            Add Credentials
          </button>

          {/* Refresh Button */}
          <button
            onClick={refreshCredentials}
            disabled={loading}
            style={{
              padding: '1rem',
              background: '#f0f9ff',
              color: '#0369a1',
              border: '2px solid #e0f2fe',
              borderRadius: '8px',
              fontSize: '1rem',
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: loading ? 0.6 : 1
            }}
            onMouseEnter={(e) => {
              if (!loading) {
                e.target.style.backgroundColor = '#0369a1';
                e.target.style.color = '#ffffff';
              }
            }}
            onMouseLeave={(e) => {
              if (!loading) {
                e.target.style.backgroundColor = '#f0f9ff';
                e.target.style.color = '#0369a1';
              }
            }}
            title="Refresh credentials"
          >
            {loading ? (
              <div style={{
                width: '16px',
                height: '16px',
                border: '2px solid #0369a1',
                borderTop: '2px solid transparent',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
            ) : (
              '🔄'
            )}
          </button>
        </div>

        {/* Stats */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingTop: '1rem',
          borderTop: '1px solid #ecf0f1',
          fontSize: '0.9rem',
          color: '#666'
        }}>
          <span>
            {loading ? 'Loading credentials...' :
              error ? 'Error loading credentials' :
                `Showing ${displayedCredentials.length} of ${totalCount} credentials`}
            {(searchTerm || roleFilter) && !loading && !error && ` (filtered from ${allCredentials.length} total)`}
          </span>
          {loading && (
            <span style={{ color: '#238548' }}>
              <div style={{
                width: '16px',
                height: '16px',
                border: '2px solid #e3f2fd',
                borderTop: '2px solid #238548',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                display: 'inline-block'
              }}></div>
            </span>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div style={{
          background: '#fee2e2',
          border: '1px solid #fecaca',
          color: '#dc2626',
          padding: '1rem',
          borderRadius: '12px',
          marginBottom: '2rem'
        }}>
          <p style={{ margin: '0 0 0.5rem 0', fontWeight: '500' }}>⚠️ Error Loading Credentials</p>
          <p style={{ margin: '0 0 1rem 0', whiteSpace: 'pre-line' }}>{error}</p>
          <button
            onClick={refreshCredentials}
            style={{
              background: '#dc2626',
              color: 'white',
              border: 'none',
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            Try Again
          </button>
        </div>
      )}

      {/* Credentials Table */}
      <div style={{
        background: '#ffffff',
        borderRadius: '12px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        {loading && allCredentials.length === 0 ? (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '4rem',
            flexDirection: 'column',
            gap: '1rem'
          }}>
            <div style={{
              width: '50px',
              height: '50px',
              border: '4px solid #e3f2fd',
              borderTop: '4px solid #238548',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
            <p style={{ color: '#666', fontSize: '1.1rem' }}>Loading credentials...</p>
          </div>
        ) : displayedCredentials.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '4rem 2rem'
          }}>
            <div style={{ fontSize: '4rem', marginBottom: '1rem', opacity: 0.5 }}>🔐</div>
            <h3 style={{
              fontSize: '1.5rem',
              fontWeight: 600,
              color: '#333',
              margin: '0 0 1rem 0'
            }}>
              {searchTerm || roleFilter ? 'No credentials found' : 'No credentials registered yet'}
            </h3>
            <p style={{ color: '#666', margin: '0 0 2rem 0', fontSize: '1rem' }}>
              {searchTerm || roleFilter
                ? 'Try adjusting your search criteria or filters'
                : 'Get started by adding your first login credentials to the system'
              }
            </p>
            {!(searchTerm || roleFilter) && (
              <button
                onClick={() => {
                  resetForm();
                  setShowAddModal(true);
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  cursor: 'pointer',
                  transition: 'transform 0.3s ease'
                }}
                onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
              >
                Add First Credentials
              </button>
            )}
          </div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: isMobile ? '0.85rem' : '0.95rem'
              }}>
                <thead>
                  <tr style={{
                    background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
                    color: '#ffffff'
                  }}>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'left',
                      fontWeight: 600,
                      borderBottom: 'none',
                      whiteSpace: 'nowrap'
                    }}>ID</th>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'left',
                      fontWeight: 600,
                      borderBottom: 'none',
                      whiteSpace: 'nowrap'
                    }}>Username</th>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'left',
                      fontWeight: 600,
                      borderBottom: 'none',
                      whiteSpace: 'nowrap'
                    }}>Role</th>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'left',
                      fontWeight: 600,
                      borderBottom: 'none',
                      whiteSpace: 'nowrap'
                    }}>Password</th>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'left',
                      fontWeight: 600,
                      borderBottom: 'none',
                      whiteSpace: 'nowrap'
                    }}>Created</th>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'center',
                      fontWeight: 600,
                      borderBottom: 'none',
                      whiteSpace: 'nowrap'
                    }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {displayedCredentials.map((credential, index) => (
                    <tr key={credential.id} style={{
                      borderBottom: '1px solid #f1f5f9',
                      transition: 'background-color 0.3s ease'
                    }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <td style={{ padding: '1rem', fontWeight: 500, color: '#238548' }}>
                        {credential.id}
                      </td>
                      <td style={{ padding: '1rem' }}>
                        <div style={{ fontWeight: 500, color: '#333' }}>
                          {credential.username}
                        </div>
                      </td>
                      <td style={{ padding: '1rem' }}>
                        <span style={{
                          padding: '0.25rem 0.75rem',
                          borderRadius: '20px',
                          fontSize: '0.8rem',
                          fontWeight: 500,
                          textTransform: 'capitalize',
                          ...getRoleBadgeColor(credential.role)
                        }}>
                          {credential.role}
                        </span>
                      </td>
                      <td style={{ padding: '1rem', color: '#666', fontFamily: 'monospace' }}>
                        {maskPassword(credential.password)}
                      </td>
                      <td style={{ padding: '1rem', color: '#666', fontSize: '0.9rem' }}>
                        {new Date(credential.created_at).toLocaleDateString()}
                      </td>
                      <td style={{ padding: '1rem', textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                          <button
                            onClick={() => handleViewClick(credential)}
                            style={{
                              padding: '0.5rem 0.75rem',
                              backgroundColor: '#f0fdf4',
                              color: '#16a34a',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '0.85rem',
                              cursor: 'pointer',
                              transition: 'all 0.3s ease',
                              fontWeight: 500
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = '#16a34a';
                              e.target.style.color = '#ffffff';
                              e.target.style.transform = 'translateY(-1px)';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = '#f0fdf4';
                              e.target.style.color = '#16a34a';
                              e.target.style.transform = 'translateY(0)';
                            }}
                          >
                            👁️ View
                          </button>
                          <button
                            onClick={() => handleEditClick(credential)}
                            style={{
                              padding: '0.5rem 0.75rem',
                              backgroundColor: '#e3f2fd',
                              color: '#1976d2',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '0.85rem',
                              cursor: 'pointer',
                              transition: 'all 0.3s ease',
                              fontWeight: 500
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = '#1976d2';
                              e.target.style.color = '#ffffff';
                              e.target.style.transform = 'translateY(-1px)';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = '#e3f2fd';
                              e.target.style.color = '#1976d2';
                              e.target.style.transform = 'translateY(0)';
                            }}
                          >
                            ✏️ Edit
                          </button>
                          <button
                            onClick={() => handleDeleteClick(credential)}
                            style={{
                              padding: '0.5rem 0.75rem',
                              backgroundColor: '#ffebee',
                              color: '#d32f2f',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '0.85rem',
                              cursor: 'pointer',
                              transition: 'all 0.3s ease',
                              fontWeight: 500
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = '#d32f2f';
                              e.target.style.color = '#ffffff';
                              e.target.style.transform = 'translateY(-1px)';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = '#ffebee';
                              e.target.style.color = '#d32f2f';
                              e.target.style.transform = 'translateY(0)';
                            }}
                          >
                            🗑️ Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{
                padding: '1.5rem',
                borderTop: '1px solid #f1f5f9',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '1rem'
              }}>
                <div style={{ color: '#666', fontSize: '0.9rem' }}>
                  Page {currentPage} of {totalPages} • {totalCount} total credentials
                  {(searchTerm || roleFilter) && ` (filtered from ${allCredentials.length})`}
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    style={{
                      padding: '0.5rem 1rem',
                      border: '2px solid #e2e8f0',
                      backgroundColor: currentPage === 1 ? '#f8fafc' : '#ffffff',
                      color: currentPage === 1 ? '#9ca3af' : '#374151',
                      borderRadius: '6px',
                      cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                      transition: 'all 0.3s ease',
                      fontSize: '0.9rem'
                    }}
                    onMouseEnter={(e) => {
                      if (currentPage !== 1) {
                        e.target.style.borderColor = '#238548';
                        e.target.style.backgroundColor = '#f0fdf4';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (currentPage !== 1) {
                        e.target.style.borderColor = '#e2e8f0';
                        e.target.style.backgroundColor = '#ffffff';
                      }
                    }}
                  >
                    ← Previous
                  </button>

                  {[...Array(Math.min(5, totalPages))].map((_, index) => {
                    let page;
                    if (totalPages <= 5) {
                      page = index + 1;
                    } else if (currentPage <= 3) {
                      page = index + 1;
                    } else if (currentPage >= totalPages - 2) {
                      page = totalPages - 4 + index;
                    } else {
                      page = currentPage - 2 + index;
                    }

                    return (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        style={{
                          padding: '0.5rem 0.75rem',
                          border: '2px solid',
                          borderColor: page === currentPage ? '#238548' : '#e2e8f0',
                          backgroundColor: page === currentPage ? '#238548' : '#ffffff',
                          color: page === currentPage ? '#ffffff' : '#374151',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          fontSize: '0.9rem',
                          fontWeight: page === currentPage ? '600' : '400'
                        }}
                        onMouseEnter={(e) => {
                          if (page !== currentPage) {
                            e.target.style.borderColor = '#238548';
                            e.target.style.backgroundColor = '#f0fdf4';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (page !== currentPage) {
                            e.target.style.borderColor = '#e2e8f0';
                            e.target.style.backgroundColor = '#ffffff';
                          }
                        }}
                      >
                        {page}
                      </button>
                    );
                  })}

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    style={{
                      padding: '0.5rem 1rem',
                      border: '2px solid #e2e8f0',
                      backgroundColor: currentPage === totalPages ? '#f8fafc' : '#ffffff',
                      color: currentPage === totalPages ? '#9ca3af' : '#374151',
                      borderRadius: '6px',
                      cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                      transition: 'all 0.3s ease',
                      fontSize: '0.9rem'
                    }}
                    onMouseEnter={(e) => {
                      if (currentPage !== totalPages) {
                        e.target.style.borderColor = '#238548';
                        e.target.style.backgroundColor = '#f0fdf4';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (currentPage !== totalPages) {
                        e.target.style.borderColor = '#e2e8f0';
                        e.target.style.backgroundColor = '#ffffff';
                      }
                    }}
                  >
                    Next →
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Add/Edit Modal */}
      {(showAddModal || showEditModal) && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '1rem'
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '12px',
            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
            width: '100%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Modal Header */}
            <div style={{
              background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
              color: '#ffffff',
              padding: '1.5rem 2rem',
              borderRadius: '12px 12px 0 0'
            }}>
              <h3 style={{
                margin: 0,
                fontSize: '1.5rem',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                {selectedCredential ? '✏️ Edit Credentials' : '➕ Add New Credentials'}
              </h3>
              <p style={{ margin: '0.5rem 0 0 0', opacity: 0.9, fontSize: '0.95rem' }}>
                {selectedCredential ? 'Update login credentials' : 'Create new user login credentials'}
              </p>
            </div>

            {/* Modal Content */}
            <div style={{
              padding: '2rem',
              overflowY: 'auto',
              flex: 1
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1.5rem' }}>
                {/* Username */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Username *
                  </label>
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    placeholder="Enter username"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: `2px solid ${formErrors.username ? '#dc2626' : '#e5e7eb'}`,
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'border-color 0.3s ease',
                      backgroundColor: formErrors.username ? '#fef2f2' : '#ffffff'
                    }}
                    onFocus={(e) => {
                      if (!formErrors.username) e.target.style.borderColor = '#238548';
                    }}
                    onBlur={(e) => {
                      if (!formErrors.username) e.target.style.borderColor = '#e5e7eb';
                    }}
                  />
                  {formErrors.username && (
                    <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                      {formErrors.username}
                    </p>
                  )}
                </div>

                {/* Password */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Password {selectedCredential ? '' : '*'} {selectedCredential && <span style={{ fontSize: '0.8rem', color: '#666' }}>(leave blank to keep current)</span>}
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder={selectedCredential ? "Enter new password" : "Enter password"}
                      style={{
                        width: '100%',
                        padding: '0.75rem 3rem 0.75rem 0.75rem',
                        border: `2px solid ${formErrors.password ? '#dc2626' : '#e5e7eb'}`,
                        borderRadius: '8px',
                        fontSize: '1rem',
                        outline: 'none',
                        transition: 'border-color 0.3s ease',
                        backgroundColor: formErrors.password ? '#fef2f2' : '#ffffff'
                      }}
                      onFocus={(e) => {
                        if (!formErrors.password) e.target.style.borderColor = '#238548';
                      }}
                      onBlur={(e) => {
                        if (!formErrors.password) e.target.style.borderColor = '#e5e7eb';
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      style={{
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        fontSize: '1.2rem',
                        cursor: 'pointer',
                        color: '#666',
                        padding: '0.5rem'
                      }}
                    >
                      {showPassword ? '👁️' : '🙈'}
                    </button>
                  </div>
                  {formErrors.password && (
                    <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                      {formErrors.password}
                    </p>
                  )}
                </div>

                {/* Role */}
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    color: '#374151',
                    marginBottom: '0.5rem'
                  }}>
                    Role *
                  </label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: `2px solid ${formErrors.role ? '#dc2626' : '#e5e7eb'}`,
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'border-color 0.3s ease',
                      backgroundColor: formErrors.role ? '#fef2f2' : '#ffffff'
                    }}
                    onFocus={(e) => {
                      if (!formErrors.role) e.target.style.borderColor = '#238548';
                    }}
                    onBlur={(e) => {
                      if (!formErrors.role) e.target.style.borderColor = '#e5e7eb';
                    }}
                  >
                    <option value="student">Student</option>
                    <option value="teacher">Teacher</option>
                    <option value="hod">HOD</option>
                    <option value="parent">Parent</option>
                    <option value="admin">Admin</option>
                  </select>
                  {formErrors.role && (
                    <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                      {formErrors.role}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div style={{
              padding: '1.5rem 2rem',
              borderTop: '1px solid #f1f5f9',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '1rem',
              backgroundColor: '#f8fafc'
            }}>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setShowEditModal(false);
                  resetForm();
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: '2px solid #e5e7eb',
                  backgroundColor: '#ffffff',
                  color: '#374151',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  fontWeight: 500
                }}
                onMouseEnter={(e) => {
                  e.target.style.borderColor = '#d1d5db';
                  e.target.style.backgroundColor = '#f9fafb';
                }}
                onMouseLeave={(e) => {
                  e.target.style.borderColor = '#e5e7eb';
                  e.target.style.backgroundColor = '#ffffff';
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                style={{
                  padding: '0.75rem 2rem',
                  border: 'none',
                  background: submitting
                    ? 'linear-gradient(135deg, #9ca3af 0%, #d1d5db 100%)'
                    : 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
                  color: '#ffffff',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
                onMouseEnter={(e) => {
                  if (!submitting) {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 8px 25px rgba(35, 133, 72, 0.3)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!submitting) {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = 'none';
                  }
                }}
              >
                {submitting ? (
                  <>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid #ffffff',
                      borderTop: '2px solid transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                    Saving...
                  </>
                ) : (
                  <>
                    {selectedCredential ? '💾 Update Credentials' : '➕ Add Credentials'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && selectedCredential && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '1rem'
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '12px',
            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
            maxWidth: '500px',
            width: '100%'
          }}>
            {/* Modal Header */}
            <div style={{
              background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
              color: '#ffffff',
              padding: '1.5rem 2rem',
              borderRadius: '12px 12px 0 0'
            }}>
              <h3 style={{
                margin: 0,
                fontSize: '1.5rem',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                👁️ View Credentials
              </h3>
              <p style={{ margin: '0.5rem 0 0 0', opacity: 0.9, fontSize: '0.95rem' }}>
                Login credential details
              </p>
            </div>

            {/* Modal Content */}
            <div style={{ padding: '2rem' }}>
              <div style={{ display: 'grid', gap: '1.5rem' }}>
                <div>
                  <label style={{ fontSize: '0.9rem', fontWeight: 600, color: '#666', display: 'block', marginBottom: '0.5rem' }}>
                    User ID
                  </label>
                  <p style={{ margin: 0, fontSize: '1.1rem', fontWeight: 500, color: '#333' }}>
                    {selectedCredential.id}
                  </p>
                </div>

                <div>
                  <label style={{ fontSize: '0.9rem', fontWeight: 600, color: '#666', display: 'block', marginBottom: '0.5rem' }}>
                    Username
                  </label>
                  <p style={{ margin: 0, fontSize: '1.1rem', fontWeight: 500, color: '#333' }}>
                    {selectedCredential.username}
                  </p>
                </div>

                <div>
                  <label style={{ fontSize: '0.9rem', fontWeight: 600, color: '#666', display: 'block', marginBottom: '0.5rem' }}>
                    Role
                  </label>
                  <span style={{
                    padding: '0.5rem 1rem',
                    borderRadius: '20px',
                    fontSize: '0.9rem',
                    fontWeight: 500,
                    textTransform: 'capitalize',
                    ...getRoleBadgeColor(selectedCredential.role)
                  }}>
                    {selectedCredential.role}
                  </span>
                </div>

                <div>
                  <label style={{ fontSize: '0.9rem', fontWeight: 600, color: '#666', display: 'block', marginBottom: '0.5rem' }}>
                    Created Date
                  </label>
                  <p style={{ margin: 0, fontSize: '1rem', color: '#666' }}>
                    {new Date(selectedCredential.created_at).toLocaleString()}
                  </p>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div style={{
              padding: '1.5rem 2rem',
              borderTop: '1px solid #f1f5f9',
              display: 'flex',
              justifyContent: 'flex-end',
              backgroundColor: '#f8fafc'
            }}>
              <button
                onClick={() => {
                  setShowViewModal(false);
                  setSelectedCredential(null);
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: '2px solid #e5e7eb',
                  backgroundColor: '#ffffff',
                  color: '#374151',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  fontWeight: 500
                }}
                onMouseEnter={(e) => {
                  e.target.style.borderColor = '#d1d5db';
                  e.target.style.backgroundColor = '#f9fafb';
                }}
                onMouseLeave={(e) => {
                  e.target.style.borderColor = '#e5e7eb';
                  e.target.style.backgroundColor = '#ffffff';
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedCredential && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '1rem'
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '12px',
            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
            maxWidth: '400px',
            width: '100%'
          }}>
            {/* Modal Header */}
            <div style={{
              padding: '2rem 2rem 1rem 2rem',
              textAlign: 'center'
            }}>
              <div style={{
                width: '64px',
                height: '64px',
                background: '#fee2e2',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1rem',
                fontSize: '2rem'
              }}>
                🗑️
              </div>
              <h3 style={{
                margin: '0 0 1rem 0',
                fontSize: '1.5rem',
                fontWeight: 600,
                color: '#dc2626'
              }}>
                Delete Credentials
              </h3>
              <p style={{
                margin: '0 0 1rem 0',
                color: '#6b7280',
                lineHeight: 1.6
              }}>
                Are you sure you want to delete the login credentials for <strong style={{ color: '#374151' }}>{selectedCredential.username}</strong>?
              </p>
              <p style={{
                margin: 0,
                color: '#dc2626',
                fontSize: '0.9rem',
                fontWeight: 500
              }}>
                This action cannot be undone and will permanently remove access for this user.
              </p>
            </div>

            {/* Modal Footer */}
            <div style={{
              padding: '1rem 2rem 2rem 2rem',
              display: 'flex',
              justifyContent: 'center',
              gap: '1rem'
            }}>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedCredential(null);
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: '2px solid #e5e7eb',
                  backgroundColor: '#ffffff',
                  color: '#374151',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  fontWeight: 500
                }}
                onMouseEnter={(e) => {
                  e.target.style.borderColor = '#d1d5db';
                  e.target.style.backgroundColor = '#f9fafb';
                }}
                onMouseLeave={(e) => {
                  e.target.style.borderColor = '#e5e7eb';
                  e.target.style.backgroundColor = '#ffffff';
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={submitting}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: 'none',
                  background: submitting
                    ? 'linear-gradient(135deg, #9ca3af 0%, #d1d5db 100%)'
                    : 'linear-gradient(135deg, #dc2626 0%, #f87171 100%)',
                  color: '#ffffff',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
                onMouseEnter={(e) => {
                  if (!submitting) {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 8px 25px rgba(220, 38, 38, 0.3)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!submitting) {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = 'none';
                  }
                }}
              >
                {submitting ? (
                  <>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid #ffffff',
                      borderTop: '2px solid transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                    Deleting...
                  </>
                ) : (
                  '🗑️ Delete Credentials'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default AdminLoginManagement;